
import { defineComponent, ref, reactive, toRefs, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import f10Api from '@/api/f10ApiHk'
import moment from 'moment'

export default defineComponent({
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    token: {
      type: String,
      default: () => {
        return ''
      }
    },
    theme: {
      type: String,
      default: () => {
        return 'light'
      }
    },
  },
  components: { Charts, Nodata },
  setup(props) {

    const route = useRoute()
    const state = reactive({
      spinning: true,
      hasData: false,
      currentPeriod: '3',
      currentValue: 0,
      chartData: {
        list: [],
        average: '',
        top: '',
        bottom: '',
        max: '',
        min: ''
      },
      periodOptions: [
        { value: '3', label: '近3个月' },
        { value: '6', label: '近6个月' },
        { value: '12', label: '近12个月' }
      ]
    })

    // 格式化单个数值：保留2位小数，负数加括号
    const formatValue = (value: string | number) => {
      const numValue = parseFloat(value.toString())
      if (numValue < 0) {
        return `(${Math.abs(numValue).toFixed(2)})`
      } else {
        return numValue.toFixed(2)
      }
    }

    // 格式化范围显示：如果任意值是负数，就只给那个负数加括号
    const formatRange = (topValue: string | number, bottomValue: string | number) => {
      const topNum = parseFloat(topValue.toString())
      const bottomNum = parseFloat(bottomValue.toString())

      const topFormatted = topNum < 0 ? `(${topNum.toFixed(2)})` : topNum.toFixed(2)
      const bottomFormatted = bottomNum < 0 ? `(${bottomNum.toFixed(2)})` : bottomNum.toFixed(2)

      return `${bottomFormatted}~${topFormatted}`
    }

    const chartOption = computed(() => {
      if (!state.chartData.list || state.chartData.list.length === 0) {
        return {}
      }

      const dates = state.chartData.list.map((item: any) => item.trading_date)
      const values = state.chartData.list.map((item: any) => parseFloat(item.value))

      // 获取最新值作为当前值（数据已按时间排序，最新值在数组末尾）
      state.currentValue = values[values.length - 1]

      // 计算滚动条起始位置（显示最新的9个数据点）
      const currentIdx = Math.max(0, dates.length - 9)

      return {
        backgroundColor: 'transparent',
        title: {
          // text: 'PE(TTM)',
          left: 'center',
          textStyle: {
            color: '#d7d7df',
            fontSize: 16,
            fontWeight: 'bold'
          }
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(30, 35, 50, 0.9)',
          borderColor: '#252a34',
          textStyle: {
            color: '#d7d7df'
          },
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#4191ff'
            }
          },
          formatter: function (params: any) {
            const date = params[0].name
            const value = params[0].value
            return `
              <div style="margin-bottom: 5px; font-weight: bold">${date}</div>
              <div>PE(TTM): <span style="color: #ffa500; font-weight: bold">${value.toFixed(2)}</span></div>
              <div>均值: <span style="color: #a033ff; font-weight: bold">${formatValue(state.chartData.average)}</span></div>
              <div>均值±1倍标准差: <span style="color: #4191ff; font-weight: bold">${formatRange(state.chartData.top, state.chartData.bottom)}</span></div>
            `
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%', // 移除滚动条后减少底部空间
          top: '60px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: {
            lineStyle: {
              color: '#4a5064'
            }
          },
          axisLabel: {
            color: '#8c94a8',
            rotate: 0,
            interval: Math.max(0, Math.floor(dates.length / 6) - 1), // 只显示6个标签
            formatter: function (value: string) {
              return moment(value).format('YYYY-MM-DD')
            }
          },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          // name: '值',
          nameTextStyle: {
            color: '#8c94a8',
            padding: [0, 40, 0, 0]
          },
          axisLine: {
            lineStyle: {
              color: '#4a5064'
            }
          },
          axisLabel: {
            color: '#8c94a8',
            formatter: (value: number) => value.toFixed(2)
          },
          splitLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#252a34' : '#DBDFE6'
            }
          },
          min: Math.min.apply(Math, values),
          max: Math.max.apply(Math, values)
        },
        series: [
          {
            // name: 'PE(TTM)',
            type: 'line',
            data: values,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: '#ffa500'
            },
            itemStyle: {
              color: '#ffa500'
            },
            markLine: {
              silent: true,
              data: [
                {
                  name: '均值',
                  yAxis: parseFloat(state.chartData.average),
                  lineStyle: {
                    color: '#a033ff',
                    width: 2,
                    type: 'dashed'
                  },
                  label: {
                    position: 'end',
                    // formatter: '均值: ' + state.chartData.average,
                    color: '#a033ff'
                  }
                },
                {
                  name: '均值+1倍标准差',
                  yAxis: parseFloat(state.chartData.top),
                  lineStyle: {
                    color: '#4191ff',
                    width: 1.5,
                    type: 'dashed'
                  },
                  label: {
                    position: 'end',
                    // formatter: '均值+1倍标准差: ' + state.chartData.top,
                    color: '#4191ff'
                  }
                },
                {
                  name: '均值-1倍标准差',
                  yAxis: parseFloat(state.chartData.bottom),
                  lineStyle: {
                    color: '#ff6b6b',

                    width: 1.5,
                    type: 'dashed'
                  },
                  label: {
                    position: 'end',
                    // formatter: '均值-1倍标准差: ' + state.chartData.bottom,
                    color: '#ff6b6b'
                  }
                }
              ]
            }
          }
        ],
        // dataZoom: [
        //   {
        //     type: 'inside',
        //     orient: 'horizontal', // 水平方向
        //     start: 0,
        //     end: 100,
        //     zoomLock: true // 锁定缩放比例
        //   },
        //   {
        //     type: 'slider',
        //     show: true,
        //     orient: 'horizontal', // 水平方向
        //     bottom: 10, // 放置在底部
        //     height: 5, // 滚动条高度
        //     start: 0,
        //     end: 100,
        //     handleSize: '100%',
        //     handleStyle: {
        //       color: props.theme === 'dark' ? '#888696' : '#D4D5D8'
        //     },
        //     textStyle: {
        //       color: props.theme === 'dark' ? '#888696' : '#D4D5D8'
        //     },
        //     borderColor: props.theme === 'dark' ? '#888696' : '#D4D5D8',
        //     fillerColor: props.theme === 'dark' ? 'rgba(136, 134, 150, 0.3)' : 'rgba(212, 213, 216, 0.3)',
        //     dataBackground: {
        //       lineStyle: {
        //         color: props.theme === 'dark' ? '#888696' : '#D4D5D8'
        //       },
        //       areaStyle: {
        //         color: props.theme === 'dark' ? 'rgba(136, 134, 150, 0.1)' : 'rgba(212, 213, 216, 0.1)'
        //       }
        //     }
        //   }
        // ]

      }
    })

    const fetchData = async (period: string) => {
      state.spinning = true

      const requestData = {
        data: {
          // exchange: 'HKEX' || route.query.market,
          // symbol: '00700' || route.query.symbol,
          // exchange: route.query.market || 'HKEX',
          exchange: route.query.exchange || 'HKEX',
          symbol: route.query.code || '00700',
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944',
          month_long: period
        },
        headers: {}
      }

      f10Api.getBriefValuationAnalysis(requestData)
        .then((res: any) => {
          if (res?.data?.NetProfTTMRatio) {
            const ratio = res.data.NetProfTTMRatio;

            // 对数据进行排序，时间从小到大
            const sortedList = (ratio.list || []).map((item: any) => ({
              trading_date: item.trading_date,
              value: item.value?.toString() || ''
            })).sort((a: any, b: any) => {
              return new Date(a.trading_date).getTime() - new Date(b.trading_date).getTime();
            });

            state.chartData = {
              list: sortedList,
              average: ratio.average?.toString() || '0',
              top: ratio.top?.toString() || '0',
              bottom: ratio.bottom?.toString() || '0',
              max: ratio.max?.toString() || '0',
              min: ratio.min?.toString() || '0'
            };

            state.hasData = true;
            console.log('PE(TTM)数据:', state.chartData);
          } else {
            state.hasData = false;
            console.warn('PE(TTM)数据结构异常');
          }
        })
        .catch((error) => {
          console.error('获取PE(TTM)数据失败:', error);
          state.hasData = false;
        })
        .finally(() => {
          state.spinning = false;
        });
    }

    const handlePeriodChange = (e: any) => {
      state.currentPeriod = e.target.value
      fetchData(state.currentPeriod)
    }

    onMounted(() => {
      fetchData(state.currentPeriod)
    })

    return {
      ...toRefs(state),
      chartOption,
      handlePeriodChange,
      formatValue,
      formatRange
    }
  }
})
